import Label from "@/v2-ui/label"
import Switch from "@/v2-ui/switch"
import { toast } from "@/v2-ui/toast"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseSeatPolicySwitchProps = {
  license: LicenseFragment
}

// Who assigns the seats on this license: Placepoint staff in Backoffice
// (off), or also the customer's own administrators in Fusion under Settings ›
// Members (on). The customer sees who holds a seat either way; the switch
// only decides whether they can change it. App-owned, so it works on a
// billing-derived license too.
function LicenseSeatPolicySwitch(props: LicenseSeatPolicySwitchProps) {
  const { license } = props

  const [updateLicense, { loading }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: data.updateLicense.SeatsManagedByCustomer
          ? "The customer's admins can now assign seats in Fusion"
          : "Seats on this license are assigned by Placepoint only",
      })
    },
    onError(error) {
      toast({
        variant: "destructive",
        title: "Seat policy not saved",
        description: error.message,
      })
    },
  })

  return (
    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2">
      <div>
        <Label htmlFor="licenseSeatsManagedByCustomer" className="text-sm">
          The customer&apos;s admins may assign seats in Fusion
        </Label>
        <p className="text-xs text-muted-foreground">
          Off: Placepoint assigns seats here and the customer only sees who
          holds one. On: their team administrators do it themselves under
          Settings › Members; going over the count is allowed and goes on the
          next invoice.
        </p>
      </div>
      <Switch
        id="licenseSeatsManagedByCustomer"
        data-feature-code="UI_BackofficeLicense_Seats_ManagedByCustomer"
        checked={license.SeatsManagedByCustomer}
        isLoading={loading}
        onCheckedChange={(checked) => {
          void updateLicense({
            variables: {
              id: license.Id,
              input: { SeatsManagedByCustomer: checked },
            },
          })
        }}
      />
    </div>
  )
}

export default LicenseSeatPolicySwitch
